import React, { useState, useEffect, useMemo } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toastMessage } from '@/utils/toast-message';
import { setReminder } from '@/actions/fetch-leads';
import { addEvent, updateEvent, DeleteEventp } from '@/actions/data-fetch-activities';
import { Calendar, Clock, Bell, Sparkles, Trash2, Save, Plus, X } from 'lucide-react';

export interface Event {
    event_title: string;
    db_start: string;
    alert_before: string;
    uuid?: string;
}

const ALERT_TIME_OPTIONS = [
    { value: '0 min', label: 'On time' },
    { value: '15 min', label: '15 minutes before' },
    { value: '30 min', label: '30 minutes before' },
    { value: '1 hour', label: '1 hour before' },
    { value: '2 hours', label: '2 hours before' },
];

// Generate time options in 15-min intervals
const TIME_OPTIONS: { value: string; label: string }[] = [];
for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
        const hh = String(h).padStart(2, '0');
        const mm = String(m).padStart(2, '0');
        const value = `${hh}:${mm}`;
        const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
        const ampm = h < 12 ? 'AM' : 'PM';
        const label = `${String(hour12).padStart(2, '0')}:${mm} ${ampm}`;
        TIME_OPTIONS.push({ value, label });
    }
}

const reminderSchema = z.object({
    event_title: z.string().min(3, 'Title must be at least 3 characters').max(100, 'Title too long'),
    start_date: z.string().min(1, 'Date is required'),
    start_time: z.string().min(1, 'Time is required'),
    alert_before: z.enum(['0 min', '15 min', '30 min', '1 hour', '2 hours']).optional(),
});

export type ReminderFormData = z.infer<typeof reminderSchema>;

interface ReminderFormProps {
    leadUuid?: string | null;
    selectedItems?: Array<{ uuid: string; [key: string]: any }>;
    selectedItemsActivities?: Event[];
    onCancel?: () => void;
    refreshData?: () => void;
    onClose?: () => void;
    showDelete: boolean;
    isActivity: boolean;
    onDelete?: (value: boolean) => void;
}

const inputCls = 'w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-transparent';

const ReminderForm: React.FC<ReminderFormProps> = ({
    leadUuid,
    selectedItems,
    selectedItemsActivities,
    showDelete,
    onCancel,
    refreshData,
    isActivity,
    onClose,
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [eventuuid, seteventuuid] = useState<string>();
    const [isEditMode, setIsEditMode] = useState(false);

    const {
        control,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
    } = useForm<ReminderFormData>({
        resolver: zodResolver(reminderSchema),
        defaultValues: {
            event_title: '',
            start_date: '',
            start_time: '09:00',
            alert_before: '0 min' as const,
        },
    });

    useEffect(() => {
        if (selectedItemsActivities && selectedItemsActivities.length > 0) {
            const selectedEvent = selectedItemsActivities[0];
            const isExisting = !!(selectedEvent.uuid && selectedEvent.event_title);
            setIsEditMode(isExisting);
            setValue('event_title', selectedEvent.event_title);

            if (selectedEvent.db_start) {
                // Normalise to "YYYY-MM-DDTHH:mm"
                const normalised = selectedEvent.db_start.slice(0, 16).replace(' ', 'T');
                const [datePart, timePart] = normalised.split('T');
                setValue('start_date', datePart || '');

                // Snap time to nearest 15-min slot
                if (timePart) {
                    const [hh, mm] = timePart.split(':').map(Number);
                    const snapped = Math.round(mm / 15) * 15;
                    const snappedMm = snapped === 60 ? 0 : snapped;
                    const snappedHh = snapped === 60 ? (hh + 1) % 24 : hh;
                    setValue('start_time', `${String(snappedHh).padStart(2, '0')}:${String(snappedMm).padStart(2, '0')}`);
                }
            }

            setValue('alert_before', (selectedEvent.alert_before as ReminderFormData['alert_before']) || '0 min');
            seteventuuid(selectedEvent.uuid);
        }
    }, [selectedItemsActivities, setValue]);

    const deleteEvent = async () => {
        if (!eventuuid) return;
        setIsSubmitting(true);
        try {
            await DeleteEventp(eventuuid);
            toastMessage('Event deleted successfully!', 'success');
            refreshData?.();
            onClose?.();
        } catch {
            toastMessage('Failed to delete event', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleActivitySubmit = async (formData: ReminderFormData) => {
        setIsSubmitting(true);
        try {
            const db_start = `${formData.start_date}T${formData.start_time}`;
            const params = {
                event_title: formData.event_title,
                db_start,
                alert_before: formData.alert_before || '0 min',
            };

            if (isEditMode && eventuuid) {
                const response = await updateEvent(eventuuid, params);
                toastMessage((response as any).message || 'Event updated!', 'success');
            } else {
                const response = await addEvent(params);
                toastMessage((response as any).message || 'Event created!', 'success');
            }

            reset();
            refreshData?.();
            onClose?.();
        } catch {
            toastMessage(isEditMode ? 'Failed to update event' : 'Failed to create event', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleLeadSubmit = async (formData: ReminderFormData) => {
        setIsSubmitting(true);
        const uuids = selectedItems?.map(i => i.uuid) || (leadUuid ? [leadUuid] : []);
        if (!uuids.length) {
            toastMessage('No items selected.', 'error');
            setIsSubmitting(false);
            return;
        }
        try {
            const response = await setReminder({
                params: {
                    leadUuid: uuids,
                    event_title: formData.event_title,
                    daterange: `${formData.start_date}T${formData.start_time}`,
                    alert_before: formData.alert_before || '0 min',
                },
            });
            toastMessage((response.data as any)?.message || 'Reminder set!', 'success');
            reset();
            refreshData?.();
            onClose?.();
        } catch {
            toastMessage('Failed to set reminder', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const onSubmitHandler = (formData: ReminderFormData) =>
        isActivity ? handleActivitySubmit(formData) : handleLeadSubmit(formData);

    const today = new Date().toISOString().slice(0, 10);
    const watchedDate = useWatch({ control, name: 'start_date' });

    // When date switches to today, reset time to next available slot
    useEffect(() => {
        if (watchedDate === today && availableTimeOptions.length > 0) {
            setValue('start_time', availableTimeOptions[0].value);
        }
    }, [watchedDate]); // eslint-disable-line react-hooks/exhaustive-deps

    const availableTimeOptions = useMemo(() => {
        if (watchedDate !== today) return TIME_OPTIONS;
        const now = new Date();
        const currentMinutes = now.getHours() * 60 + now.getMinutes();
        return TIME_OPTIONS.filter(opt => {
            const [h, m] = opt.value.split(':').map(Number);
            return h * 60 + m > currentMinutes;
        });
    }, [watchedDate, today]);

    return (
        <div className="bg-white rounded-xl overflow-hidden">
            {/* Header */}
            <div className="bg-brandColor px-5 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3 text-white">
                    <div className="bg-white/20 p-1.5 rounded-lg">
                        <Calendar className="w-5 h-5" />
                    </div>
                    <div>
                        <h3 className="text-base font-semibold">
                            {isEditMode ? 'Edit Event' : 'Create Event'}
                        </h3>
                        <p className="text-white/80 text-xs">
                            {isEditMode ? 'Update event details' : 'Schedule a new event or reminder'}
                        </p>
                    </div>
                </div>
                {onClose && (
                    <button onClick={onClose} className="text-white/70 hover:text-white transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                )}
            </div>

            <form onSubmit={handleSubmit(onSubmitHandler)} className="p-5 space-y-4">
                {/* Title */}
                <div>
                    <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1">
                        <Sparkles className="w-3.5 h-3.5 text-brandColor" />
                        Event Title <span className="text-red-500">*</span>
                    </label>
                    <Controller
                        name="event_title"
                        control={control}
                        render={({ field }) => (
                            <input
                                {...field}
                                type="text"
                                placeholder="e.g., Follow up call, Team meeting..."
                                className={inputCls}
                            />
                        )}
                    />
                    {errors.event_title && <p className="text-xs text-red-500 mt-1">{errors.event_title.message}</p>}
                </div>

                {/* Date + Time side by side */}
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1">
                            <Calendar className="w-3.5 h-3.5 text-brandColor" />
                            Date <span className="text-red-500">*</span>
                        </label>
                        <Controller
                            name="start_date"
                            control={control}
                            render={({ field }) => (
                                <input
                                    {...field}
                                    type="date"
                                    min={today}
                                    className={inputCls}
                                />
                            )}
                        />
                        {errors.start_date && <p className="text-xs text-red-500 mt-1">{errors.start_date.message}</p>}
                    </div>

                    <div>
                        <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1">
                            <Clock className="w-3.5 h-3.5 text-brandColor" />
                            Time <span className="text-red-500">*</span>
                        </label>
                        <Controller
                            name="start_time"
                            control={control}
                            render={({ field }) => (
                                <select {...field} className={inputCls}>
                                    {availableTimeOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            )}
                        />
                        {errors.start_time && <p className="text-xs text-red-500 mt-1">{errors.start_time.message}</p>}
                    </div>
                </div>

                {/* Alert Before */}
                <div>
                    <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1">
                        <Bell className="w-3.5 h-3.5 text-amber-500" />
                        Remind me
                    </label>
                    <Controller
                        name="alert_before"
                        control={control}
                        render={({ field }) => (
                            <select {...field} className={inputCls}>
                                {ALERT_TIME_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        )}
                    />
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <div>
                        {showDelete && eventuuid && (
                            <button
                                type="button"
                                onClick={deleteEvent}
                                disabled={isSubmitting}
                                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                            >
                                <Trash2 className="w-4 h-4" />
                                Delete
                            </button>
                        )}
                    </div>
                    <div className="flex gap-2">
                        {onCancel && (
                            <button
                                type="button"
                                onClick={onCancel}
                                disabled={isSubmitting}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                        )}
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-brandColor rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                        >
                            {isSubmitting ? (
                                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : isEditMode ? (
                                <Save className="w-4 h-4" />
                            ) : (
                                <Plus className="w-4 h-4" />
                            )}
                            {isSubmitting ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Event' : 'Create Event')}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default ReminderForm;
